var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, FormControlLabel, Paper, Radio, RadioGroup, Stack, TextField, Typography } from '@mui/material';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FeatureFlags } from '../../config/featureFlags';
import PreviewReferralOrder from '../PreviewReferralOrder';
import { ActionOption, StudyType } from '../../@types/HomePage';
import { TenantType } from '../../@types/Tenant';
import { setShowAssessmentHistory, setHasActiveSleepOrder } from '../../store/ui/uiSlice';
import { fetchPatientTabs } from '../../store/tabs/tabsSlice';
import { fetchPatientResources } from '../../store/patientResources/patientResourcesSlice';
import { setSubmitting, setSubmitSuccess, setError as setSubmitError, clearError as clearSubmitError } from '../../store/referralOrder/referralOrderSlice';
import styles from './ManageOsaRisk.module.scss';
import { submitEhrOrderWriteback, submitEpicOrderReferral } from '../../services/referralOrderService';
import { fetchTenant, sleepReferralExclusions } from '../../services/tenantService';
import { trackPendoEvent } from '../../services/pendoService';
import { ORDER_SUBMITTED, REFERRAL_SUBMITTED } from '../../constants/pendoEvents';
const ManageOsaRisk = () => {
    const dispatch = useDispatch();
    const { patientId, tenantId, encounterId, providerCode } = useSelector((state) => state.smart);
    const { loading: patientResourcesLoading, ready: patientResourcesReady, error: patientResourcesError } = useSelector((state) => state.patientResources);
    const { patientAlias } = useSelector((state) => state.osaRisk);
    const { submitting, error: submitError } = useSelector((state) => state.referralOrder);
    const { t } = useTranslation('translation', {
        keyPrefix: 'MANAGE_OSA_RISK'
    });
    const flags = useFlags();
    const isReferToSleepSpecialistEnabled = flags[FeatureFlags.REFER_TO_SLEEP_SPECIALIST] === true;
    const [tenantType, setTenantType] = useState(null);
    const isSleepReferralDisabled = !isReferToSleepSpecialistEnabled || (tenantType ? sleepReferralExclusions.includes(tenantType) : false);
    const [action, setAction] = useState(ActionOption.Refer);
    const [studyType, setStudyType] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    // VirtuOx only fulfills home sleep tests, so PSG is hidden when it's the sleep-study provider.
    const isHstOnlyTenant = providerCode === 'virtuox';
    // Fetch tenant config to determine tenant type
    useEffect(() => {
        if (!tenantId)
            return;
        let cancelled = false;
        fetchTenant(tenantId)
            .then((tenant) => {
            if (cancelled)
                return;
            setTenantType(tenant.type);
        })
            .catch(() => {
            if (cancelled)
                return;
        });
        return () => {
            cancelled = true;
        };
    }, [tenantId]);
    useEffect(() => {
        if (!patientId) {
            return;
        }
        // Don't re-fetch while a submit is in flight — avoids a TOCTOU race where
        // the resources slice resets to loading mid-submission.
        if (submitting)
            return;
        dispatch(fetchPatientResources(Object.assign({ patientId }, (tenantId ? { tenantId } : {}))));
    }, [patientId, tenantId, submitting, dispatch]);
    const getReasonText = useCallback((selectedAction = action) => {
        return selectedAction === ActionOption.Order ? t('DEFAULT_ORDER_REASON') : t('DEFAULT_REFERRAL_REASON');
    }, [action, t]);
    const [reason, setReason] = useState(() => getReasonText());
    // HST is the only valid study type for VirtuOx, so derive it directly instead of
    // resetting to null and letting a separate effect correct it on the next render.
    const getDefaultStudyType = useCallback((nextAction) => (nextAction === ActionOption.Order && isHstOnlyTenant ? StudyType.HST : null), [isHstOnlyTenant]);
    // Force action to Order when referrals are disabled for this tenant
    useEffect(() => {
        if (isSleepReferralDisabled && action === ActionOption.Refer) {
            setAction(ActionOption.Order);
            setStudyType(getDefaultStudyType(ActionOption.Order));
            setReason(getReasonText(ActionOption.Order));
        }
    }, [isSleepReferralDisabled, action, getReasonText, getDefaultStudyType]);
    const handleActionChange = (event) => {
        const value = event.target.value;
        if (value === ActionOption.Refer || value === ActionOption.Order) {
            setAction(value);
            // Reset study type when switching actions so we don't carry stale selections.
            setStudyType(getDefaultStudyType(value));
        }
    };
    const handleStudyTypeChange = (event) => {
        const value = event.target.value;
        if (value === StudyType.PSG || value === StudyType.HST) {
            setStudyType(value);
        }
    };
    const handlePreviewClick = () => {
        if (!patientResourcesReady)
            return;
        setPreviewOpen(true);
    };
    const handlePreviewClose = () => {
        setPreviewOpen(false);
    };
    const handlePrimaryAction = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!patientResourcesReady || !patientId)
            return;
        if (action === ActionOption.Order && !studyType)
            return;
        dispatch(clearSubmitError());
        if (action === ActionOption.Order && !tenantId) {
            dispatch(setSubmitError(t('ERROR_MISSING_TENANT')));
            return;
        }
        if (action === ActionOption.Order && tenantType === TenantType.ATHENA && !encounterId) {
            dispatch(setSubmitError(t('ERROR_MISSING_ENCOUNTER')));
            return;
        }
        dispatch(setSubmitting(true));
        try {
            if (action === ActionOption.Order) {
                trackPendoEvent(ORDER_SUBMITTED, { patientAlias, studyType });
                yield submitEhrOrderWriteback(patientId, tenantId, encounterId !== null && encounterId !== void 0 ? encounterId : undefined);
            }
            else if (action === ActionOption.Refer) {
                trackPendoEvent(REFERRAL_SUBMITTED, { patientAlias });
                yield submitEpicOrderReferral(patientId);
            }
            dispatch(setSubmitSuccess());
            dispatch(setShowAssessmentHistory(true));
            dispatch(setHasActiveSleepOrder(true));
            // Re-fetch tabs so the "Status" tab becomes visible after order placement
            if (patientId) {
                dispatch(fetchPatientTabs({ pageName: 'HomePage', patientId }));
            }
        }
        catch (err) {
            dispatch(setSubmitError(err instanceof Error ? err.message : 'Failed to submit order'));
        }
    });
    const previewLabel = action === ActionOption.Refer ? t('PREVIEW_REFERRAL_LETTER') : t('PREVIEW_ORDER_REPORT');
    const primaryLabel = action === ActionOption.Refer ? t('REFER_ACTION') : t('ORDER_ACTION');
    const requireStudyType = action === ActionOption.Order;
    const primaryDisabled = !patientResourcesReady || (requireStudyType && !studyType) || submitting;
    const previewDisabled = !patientResourcesReady;
    return (_jsxs(Paper, { className: styles.manage_card, elevation: 1, sx: { borderRadius: '40px' }, children: [_jsxs(Box, { sx: { mb: 3 }, children: [_jsx(Typography, { variant: "h3", sx: { mb: 1 }, children: t('TITLE') }), _jsx(Typography, { variant: "body1", color: "text.secondary", children: t('DESCRIPTION') })] }), !patientResourcesLoading && patientResourcesError && (_jsx(Alert, { severity: "error", variant: "outlined", sx: (theme) => ({
                    mb: 3,
                    borderRadius: '8px',
                    borderColor: theme.palette.error.main,
                    '& .MuiAlert-icon': { color: theme.palette.error.main }
                }), children: patientResourcesError })), submitError && (_jsx(Alert, { severity: "error", variant: "outlined", sx: (theme) => ({
                    mb: 3,
                    borderRadius: '8px',
                    borderColor: theme.palette.error.main,
                    '& .MuiAlert-icon': { color: theme.palette.error.main }
                }), children: submitError })), _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 2 }, children: t('ACTIONS') }), _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', mb: 3 }, children: [_jsxs(RadioGroup, { row: true, value: action, onChange: handleActionChange, "aria-label": t('ARIA_ACTIONS'), children: [!isSleepReferralDisabled && _jsx(FormControlLabel, { value: ActionOption.Refer, control: _jsx(Radio, {}), label: t('REFER_TO_SPECIALIST') }), _jsx(FormControlLabel, { value: ActionOption.Order, control: _jsx(Radio, {}), label: t('ORDER_SLEEP_STUDY') })] }), action === ActionOption.Refer && (_jsx(Typography, { variant: "body1", color: "text.secondary", children: t('EPIC_ADDITIONAL_INFO') }))] }), action === ActionOption.Order && (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 2 }, children: t('STUDY_TYPE') }), _jsxs(RadioGroup, { row: true, value: studyType !== null && studyType !== void 0 ? studyType : '', onChange: handleStudyTypeChange, "aria-label": t('ARIA_STUDY_TYPE'), sx: { mb: 3 }, children: [!isHstOnlyTenant && _jsx(FormControlLabel, { value: StudyType.PSG, control: _jsx(Radio, {}), label: "Polysomnography (PSG)" }), _jsx(FormControlLabel, { value: StudyType.HST, control: _jsx(Radio, {}), label: "Home Sleep Test (HST)" })] })] })), _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 2 }, children: t('REASON') }), _jsx(TextField, { multiline: true, rows: 6, fullWidth: true, value: reason, onChange: (e) => setReason(e.target.value), variant: "outlined", sx: { mb: 3 } }), _jsxs(Stack, { direction: "row", justifyContent: "space-between", alignItems: "center", children: [_jsx(Button, { variant: "text", startIcon: _jsx(ArticleOutlinedIcon, {}), sx: Object.assign({ color: 'var(--color-brand) !important' }, (previewDisabled ? { opacity: 0.5 } : {})), disabled: previewDisabled, onClick: handlePreviewClick, children: previewLabel }), _jsx(Button, { variant: "contained", size: "large", disabled: primaryDisabled, onClick: handlePrimaryAction, sx: {
                            minWidth: '145px',
                            width: 'auto',
                            height: '42px',
                            px: '16px',
                            borderRadius: '40px',
                            borderTop: '1px solid transparent',
                            border: '1px solid var(--color-brand) !important',
                            backgroundColor: 'var(--color-brand) !important',
                            textTransform: 'none',
                            boxShadow: 'none',
                            '&:hover': {
                                backgroundColor: 'var(--color-brand)',
                                borderColor: 'var(--color-brand) !important',
                                boxShadow: 'none'
                            },
                            '&.Mui-disabled': {
                                backgroundColor: 'color-mix(in srgb, var(--color-brand) 50%, transparent) !important',
                                borderColor: 'var(--color-brand) !important',
                                color: 'rgba(255, 255, 255, 0.9)'
                            }
                        }, children: submitting ? _jsx(CircularProgress, { size: 24, sx: { color: 'white' } }) : primaryLabel })] }), patientId && (_jsx(PreviewReferralOrder, { open: previewOpen, onClose: handlePreviewClose, patientId: patientId, requestBody: Object.assign({ patientId: patientId, reason,
                    action }, (studyType ? { studyType } : {})) }))] }));
};
export default ManageOsaRisk;
